import { z } from 'zod';
import { idSchema, varietyCodeSchema, weekNumberSchema } from './common.js';
import { userRoleSchema } from './auth.js';
/*
 * Admin-only payloads: the grower list and the variety harvest weeks (which weeks each variety
 * is in harvest, per season). Everything under /api/admin requires role `admin`.
 */
/** A grower account and the farms it is linked to (user_farms). */
export const growerSchema = z.object({
    id: idSchema,
    displayName: z.string().min(1),
    username: z.string().min(1),
    farmIds: z.array(idSchema),
});
export const growerListResponseSchema = z.object({ growers: z.array(growerSchema) });
/* ---------- variety harvest weeks ---------- */
export const harvestWeekSchema = z.object({
    id: idSchema,
    seasonId: idSchema,
    varietyCode: varietyCodeSchema,
    weekNumber: weekNumberSchema,
});
export const harvestWeekListResponseSchema = z.object({
    harvestWeeks: z.array(harvestWeekSchema),
});
export const listHarvestWeeksQuerySchema = z.object({ seasonId: idSchema.optional() });
/* ---------- Excel import ----------
 * The phone reads the Excel file and sends the raw cell values; the API checks every row
 * against the database. `commit: false` only checks; `commit: true` checks again, then deletes
 * every variety harvest week and adds the rows from the file in one transaction. A file with
 * rows that need fixing is never committed, so a bad row cannot silently drop a week.
 */
const rawCell = z.union([z.string(), z.number()]).nullable().optional();
const rowNumber = z.number().int().min(1);
export const harvestWeekImportRowSchema = z.object({
    row: rowNumber,
    season: rawCell,
    varietyCode: rawCell,
    weekNumber: rawCell,
});
export const harvestWeekImportRequestSchema = z.object({
    commit: z.boolean().default(false),
    rows: z.array(harvestWeekImportRowSchema).min(1).max(5000),
});
/** Why a row cannot be added. The client turns these into copy that states the fix. */
export const importErrorReasonSchema = z.enum([
    'missing_value',
    'unknown_variety',
    'unknown_season',
    'invalid_week',
    'week_not_in_season',
]);
export const importErrorSchema = z.object({
    row: rowNumber,
    reason: importErrorReasonSchema,
    /** The offending value as written in the file, for the row title. */
    value: z.string(),
    /** For week_not_in_season: the season whose weeks the row must use. */
    seasonId: idSchema.optional(),
});
export const harvestWeekImportResultSchema = z.object({
    ready: z.array(z.object({
        row: rowNumber,
        seasonId: idSchema,
        varietyCode: varietyCodeSchema,
        weekNumber: weekNumberSchema,
    })),
    errors: z.array(importErrorSchema),
    /** Rows that repeat an earlier row of the file. Added once. */
    skipped: z.array(z.object({
        row: rowNumber,
        sameAsRow: rowNumber,
        seasonId: idSchema,
        varietyCode: varietyCodeSchema,
        weekNumber: weekNumberSchema,
    })),
    /** Variety harvest weeks in the database before this import; all of them are replaced. */
    existing: z.number().int().min(0),
    /**
     * Labels of active seasons the file has no row for. An import replaces every harvest week, so
     * such a file would delete those seasons' weeks; it is refused while this is not empty.
     */
    missingSeasons: z.array(z.string().min(1)),
    /** Rows added. Zero unless `commit` was true. */
    added: z.number().int().min(0),
});
/* ---------- users and farms, maintained from one Excel file ---------- */
/**
 * One row of the user/farm file: a farm, and the user who captures for it. A user appears once
 * per farm they manage, so their details repeat; only (User_name, PUC Code) must be unique.
 *
 * Unlike the harvest weeks, nothing is deleted. A row taken out of the file turns the matching
 * assignment, and any user or farm left with none, inactive. Estimates reference farms.id and
 * users.id, so the history has to stay.
 */
export const userFarmImportRowSchema = z.object({
    row: rowNumber,
    farmName: rawCell,
    puc: rawCell,
    displayName: rawCell,
    username: rawCell,
    /** Blank leaves an existing password alone; a new user without one is an error. */
    password: rawCell,
    role: rawCell,
    active: rawCell,
});
export const userFarmImportRequestSchema = z.object({
    commit: z.boolean().default(false),
    rows: z.array(userFarmImportRowSchema).min(1).max(5000),
});
export const userFarmErrorReasonSchema = z.enum([
    'missing_value',
    'invalid_role',
    'invalid_active',
    'duplicate_assignment',
    'password_required',
]);
export const userFarmErrorSchema = z.object({
    row: rowNumber,
    reason: userFarmErrorReasonSchema,
    /** The offending value as written in the file, for the row title. */
    value: z.string(),
    /** For duplicate_assignment: the earlier row holding the same user and farm. */
    sameAsRow: rowNumber.optional(),
});
/** What an import would do, so the admin sees the effect before committing. */
export const userFarmImportResultSchema = z.object({
    ready: z.array(z.object({
        row: rowNumber,
        farmName: z.string().min(1),
        puc: z.string().min(1),
        displayName: z.string().min(1),
        username: z.string().min(1),
        role: userRoleSchema,
        active: z.boolean(),
        /** True when this row carries a new password to hash. The value itself never leaves here. */
        setsPassword: z.boolean(),
        newUser: z.boolean(),
        newFarm: z.boolean(),
    })),
    errors: z.array(userFarmErrorSchema),
    /** Assignments in the database that the file leaves out; they become inactive. */
    deactivating: z.array(z.object({ puc: z.string().min(1), username: z.string().min(1) })),
    /**
     * True when no row keeps an active admin. Everything the file leaves out goes inactive, so
     * such a file would lock every admin out; it is refused while this is true.
     */
    noActiveAdmin: z.boolean(),
    /** Users and farms the file would add. */
    newUsers: z.number().int().min(0),
    newFarms: z.number().int().min(0),
    /** Rows applied. Zero unless `commit` was true. */
    applied: z.number().int().min(0),
});
/** One assignment as the admin screen lists it. The password is never part of this. */
export const userFarmRowSchema = z.object({
    farmName: z.string().min(1),
    puc: z.string().min(1),
    displayName: z.string().min(1),
    username: z.string().min(1),
    role: userRoleSchema,
    active: z.boolean(),
});
export const userFarmListResponseSchema = z.object({ userFarms: z.array(userFarmRowSchema) });
//# sourceMappingURL=admin.js.map